import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";

import { useRecurringRequestPreviewRefresh } from "@/features/RecurringRequests/RecurringRequestPreviewProvider";
import { useMutationWithError } from "@/utils/hooks/useMutationWithError";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";

import {
  SubmitPatronRequestSeriesInput,
  useSubmitPatronRequestSeriesMutation,
} from "./__generated__/useSubmitPatronRequestSeriesMutation.graphql";
import { useDraftBookingConfirmationCompleted } from "./useConfirmDraftBooking";

export const useSubmitPatronRequestSeries = (membershipTypeId: string) => {
  const [commit, isSubmitting] =
    useMutationWithError<useSubmitPatronRequestSeriesMutation>(mutation);
  const onCompleted = useDraftBookingConfirmationCompleted();
  const { refresh: refreshPreview } = useRecurringRequestPreviewRefresh();
  const { showToast } = useToast();

  const submit = (
    input: SubmitPatronRequestSeriesInput,
    onSuccess: (isStandaloneRequest: boolean) => void
  ) => {
    commit({
      variables: { input, membershipTypeId, bookingType: "STANDARD" },
      onCompleted: (response) => {
        const booking = response.responsePayload?.booking;
        const isStandaloneRequest = booking?.bookingSeries == null;

        onCompleted(
          response.responsePayload?.success,
          booking,
          () => onSuccess(isStandaloneRequest),
          true
        );
      },
      onCompletedErrorCustom: (errors, response) => {
        const committedWarning = errors.find(
          (error) =>
            error.code === "recurring_request_series_post_commit_incomplete"
        );

        if (committedWarning) {
          showToast({
            description:
              committedWarning.message ??
              t`Your recurring request was submitted, but some follow-up updates are still pending.`,
            semanticStyle: "warning",
            toastId: "submit-patron-request-series-post-commit-warning",
          });
          const booking = response.responsePayload?.booking;
          const isStandaloneRequest = booking?.bookingSeries == null;

          onCompleted(
            true,
            booking,
            () => onSuccess(isStandaloneRequest),
            true
          );
          return errors.filter((error) => error !== committedWarning);
        }

        refreshPreview();
        return errors;
      },
    });
  };

  return [submit, isSubmitting] as const;
};

const mutation = graphql`
  mutation useSubmitPatronRequestSeriesMutation(
    $input: SubmitPatronRequestSeriesInput!
    $membershipTypeId: ID!
    $bookingType: BookingType!
  ) {
    responsePayload: submitPatronRequestSeries(input: $input) {
      success
      errors {
        field
        code
        message
      }
      booking {
        id
        bookingSeries {
          kind
        }
        ...promoCodeAnalytics_booking
        ...buildBookingConfirmationAnalyticsData_booking
        ...BookingReservation_Booking
        ...BookingInfo_Booking
        ...RoundList_Booking
        ...BookingHeaderFragment_booking
        ...CancelBookingButton_Booking
        ...RecurringRequestSeriesCard_booking
      }
    }
  }
`;
